<script setup lang="ts">
import { VImg } from '@hatfieldmedia/hmcore'
import BaseModal from './BaseModal.vue'
import BaseButton from './BaseButton.vue'

export interface ServiceData {
  serviceType: string
  badge: string
  title: string
  description: string
  features: string[]
  closingText: string
  image: string
}

defineProps<{
  modelValue: boolean
  service: ServiceData | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  register: []
  contact: []
}>()

function close() {
  emit('update:modelValue', false)
}

function onRegister() {
  close()
  emit('register')
}

function onContact() {
  close()
  emit('contact')
}
</script>

<template>
  <BaseModal :model-value="modelValue" max-width="620px" @update:model-value="close">
    <template v-if="service">
      <VImg v-if="service.image" class="lm-hero" :src="service.image" />
      <div class="lm-body">
        <div class="lm-badge">{{ service.badge }}</div>
        <h2>{{ service.title }}</h2>
        <p>{{ service.description }}</p>
        <ul class="lm-features">
          <li v-for="(feature, i) in service.features" :key="i">
            <span class="lm-check">&#10003;</span>
            {{ feature }}
          </li>
        </ul>
        <p class="lm-closing">{{ service.closingText }}</p>
        <div class="lm-cta-row">
          <BaseButton v-if="service.serviceType === 'combine'" @click="onRegister">Register Now</BaseButton>
          <BaseButton variant="outline" @click="onContact">Contact Us</BaseButton>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<style scoped>
.lm-hero {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 20px 20px 0 0;
  filter: brightness(0.65);
}

.lm-body {
  padding: 32px;
}

.lm-body h2 {
  font-family: 'HND', sans-serif;
  font-weight: 950;
  font-size: 1.6rem;
  color: var(--white);
  text-transform: uppercase;
  margin-bottom: 6px;
}

.lm-badge {
  display: inline-block;
  font-family: 'HND', sans-serif;
  font-size: 0.68rem;
  color: var(--pink-bright);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 16px;
}

.lm-body p {
  font-family: 'HNT', sans-serif;
  font-size: 0.92rem;
  color: var(--text);
  line-height: 1.75;
  margin-bottom: 16px;
}

.lm-features {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
}

.lm-features li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-family: 'HNT', sans-serif;
  font-size: 0.88rem;
  color: var(--text);
  line-height: 1.6;
}

.lm-features li:last-child {
  border-bottom: none;
}

.lm-check {
  color: var(--pink-bright);
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.lm-closing {
  font-style: italic;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.88rem;
}

.lm-cta-row {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.lm-cta-row :deep(.base-btn) {
  flex: 1;
  text-align: center;
  font-size: 0.85rem;
  padding: 13px 20px;
  border-radius: 30px;
  font-family: 'HND', sans-serif;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  transition: all 0.3s;
}

.lm-cta-row :deep(.btn-primary) {
  filter: brightness(1.12);
}

.lm-cta-row :deep(.btn-primary:hover) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(208, 23, 122, 0.35);
}

.lm-cta-row :deep(.btn-outline) {
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.lm-cta-row :deep(.btn-outline:hover) {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.35);
}

@media (max-width: 768px) {
  .lm-hero {
    height: 160px;
  }

  .lm-body {
    padding: 20px;
  }

  .lm-body h2 {
    font-size: 1.3rem;
  }

  .lm-cta-row {
    flex-direction: column;
  }

  .lm-cta-row :deep(.base-btn) {
    width: 100%;
  }
}
</style>
